/**
 * Phase C1.3 — Web Push (VAPID) client helper.
 *
 * Two responsibilities:
 *  1. Convert the base64url-encoded VAPID public key the backend serves at
 *     `/api/public/web-push-config` into the `Uint8Array` shape that
 *     `pushManager.subscribe` requires.
 *  2. Drive the full enable-flow: permission prompt → SW register →
 *     pushManager.subscribe(applicationServerKey) → POST to
 *     `/api/me/push-subscriptions`.
 *
 * The flow degrades cleanly on browsers that don't expose `serviceWorker`
 * or `PushManager` — the caller surfaces `pushSupportStatus()` so the UI
 * can disable the button instead of throwing.
 */
import api from "@/lib/api";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_API = `${BACKEND_URL}/api`;

export function pushSupportStatus() {
  if (typeof window === "undefined") return "unsupported";
  if (!("serviceWorker" in navigator)) return "unsupported";
  if (!("PushManager" in window)) return "unsupported";
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission;   // "default" | "granted" | "denied"
}

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Fetches the public VAPID key once. Cached on `window` for the session. */
export async function fetchPushConfig() {
  if (window.__entrybook_push_config) return window.__entrybook_push_config;
  // Public endpoint — no auth header needed; use plain axios so an absent
  // admin JWT doesn't trigger a 401.
  const { data } = await axios.get(`${PUBLIC_API}/public/web-push-config`);
  window.__entrybook_push_config = data;
  return data;
}

/** Returns the existing subscription (if any) on the active SW registration. */
export async function getExistingPushSubscription() {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/**
 * Full enable flow. Returns the subscription endpoint on success, or throws
 * an Error with one of the codes:
 *   PUSH_UNSUPPORTED · PUSH_DISABLED · PUSH_PERMISSION_DENIED ·
 *   PUSH_NO_SW · PUSH_SUBSCRIBE_FAILED.
 */
export async function enablePushSubscription() {
  if (pushSupportStatus() === "unsupported") {
    throw new Error("PUSH_UNSUPPORTED");
  }
  const cfg = await fetchPushConfig();
  if (!cfg.enabled || !(cfg.vapid_public_key || cfg.public_key)) {
    throw new Error("PUSH_DISABLED");
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    const err = new Error("PUSH_PERMISSION_DENIED");
    err.permission = permission;
    throw err;
  }
  const reg = await navigator.serviceWorker.getRegistration()
    || await navigator.serviceWorker.register("/sw.js");
  if (!reg) throw new Error("PUSH_NO_SW");

  // If we already have one for THIS public key, reuse it (idempotent on the
  // server side; lets the user "click enable" twice without errors).
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(cfg.vapid_public_key || cfg.public_key),
      });
    } catch (e) {
      const err = new Error("PUSH_SUBSCRIBE_FAILED");
      err.cause = e?.message || String(e);
      throw err;
    }
  }

  const subJson = sub.toJSON();
  await api.post("/me/push-subscriptions", {
    endpoint: subJson.endpoint,
    keys: subJson.keys,
  });
  return subJson.endpoint;
}

/** Best-effort disable: unsubscribe locally + DELETE from the server. */
export async function disablePushSubscription() {
  if (!("serviceWorker" in navigator)) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (!sub) return false;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  // Find our server-side row by the endpoint and DELETE it.
  try {
    const list = await api.get("/me/push-subscriptions").catch(() => null);
    const rows = list?.data || [];
    const row = rows.find((r) => r.endpoint === endpoint);
    if (row?.id) await api.delete(`/me/push-subscriptions/${row.id}`);
  } catch { /* swallow — the local unsubscribe is the source of truth */ }
  return true;
}
